import { Box, Stack } from "@mui/material";
import React, { Fragment, useState } from "react";
import { FormTextField } from "../../../components/generic/GenericTextFields";
import { SettingsFormLayout } from "./SettingsFormLayout";
import { useFormik } from "formik";
import { object, string } from "yup";
import { generateMailChangeEmail, resendEmailChangeEmail } from "../../../config/apisauce";
import { GenericDialog } from "../../../components/generic/GenericDialog";
import useStore from "../../../states/store";
import { GenericCircleIcon } from "../../../components/generic/GenericCircleIcon";
import { EnvelopeOpen } from "@phosphor-icons/react";
import { GenericResendVerificationForm } from "../../../components/ui/topbar/generic/GenericResendVerificationForm";
import { NeedHelp } from "../../../components/ui/topbar/generic/GenericDialogFooter";
import useNotiStack from "../../../hooks/useNotiStack";
import { useTheme } from "@emotion/react";

export const UpdateEmailForm = () => {
  const { success } = useNotiStack();
  const theme = useTheme();
  const [{ loaderState }, { openLoader, closeLoader }] = useStore();
  const [openEmailVerificationDialog, setOpenEmailVerificationDialog] = useState(false);
  const [loadingSend, setLoadingSend] = useState(false);

  const handleUpdateEmail = async (values) => {
    openLoader();
    const response = await generateMailChangeEmail(values);
    if (response.ok) {
      setOpenEmailVerificationDialog(true);
    }
    closeLoader();
  };

  const handleCloseEmailVerificationDialog = () => {
    formik.handleReset();
    setOpenEmailVerificationDialog(false);
  };

  const formik = useFormik({
    initialValues: {},
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: object().shape({
      'current_email': string().email('Invalid email format').required('Current email is required'),
      'email': string().email('Invalid email format').required('New email is required'),
      'email_confirmation': string().email('Invalid email format')
        .test('emails-match', 'Emails must match', function (value) {
          const { email } = this.parent;
          return value === email;
        }),
    }),
    onSubmit: (values) => handleUpdateEmail(values),
  });


  const resend = async () => {
    setLoadingSend(true);
    const response = await resendEmailChangeEmail({ email: formik?.values?.email });
    if (response.ok) {
      success('Verification resent!');
    }
    setLoadingSend(false);
  };

  return (
    <SettingsFormLayout
      title={'Email'}
      subTitle={'Manage your email address for account communications and notifications.'}
      buttonText={'Update Email'}
      action={() => formik.handleSubmit()}
      disabled={loaderState}
    >
      <Stack gap={2}>
        <FormTextField
          value={formik?.values?.current_email}
          handleChange={(value) => formik.setFieldValue('current_email', value)}
          label={'Current Email'}
          placeholder={'current@email.com'}
          error={Boolean(formik?.errors?.current_email)}
          helperText={formik?.errors?.current_email}
        />
        <FormTextField
          value={formik?.values?.email}
          handleChange={(value) => formik.setFieldValue('email', value)}
          label={'New Email'}
          placeholder={'new@email.com'}
          error={Boolean(formik?.errors?.email)}
          helperText={formik?.errors?.email}
        />
        <FormTextField
          value={formik?.values?.email_confirmation}
          handleChange={(value) => formik.setFieldValue('email_confirmation', value)}
          label={'Confirm New Email'}
          placeholder={'new@email.com'}
          error={Boolean(formik?.errors?.email_confirmation)}
          helperText={formik?.errors?.email_confirmation}
        />
      </Stack>
      <GenericDialog
        title={'Verify Your E-mail'}
        openDialog={openEmailVerificationDialog}
        handleClose={() => handleCloseEmailVerificationDialog()}
        width={'550px'}
      >
        <Fragment>
          <Box display={'flex'} justifyContent={'center'} alignItems={'center'} mb={2}>
            <GenericCircleIcon>
              <EnvelopeOpen size={96} weight="thin" color={theme.palette.primary.main} />
            </GenericCircleIcon>
          </Box>
          <Stack gap={3} alignItems={'center'}>
            <GenericResendVerificationForm loadingSend={loadingSend} resend={resend} email={formik?.values?.email} />
            <NeedHelp />
          </Stack>
        </Fragment>
      </GenericDialog>
    </SettingsFormLayout>
  );
};